import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Plus, Trash2 } from 'lucide-react';
import { fetchReferentiels, updateVetementsAdmin } from '../services/api';
import SaisieTailles from './SaisieTailles';

type Vetement = { code?: string; label: string; tailles: string[] };

/**
 * Le bloc « Tailles de vêtements » des Paramètres : la liste des textiles
 * proposés aux bénévoles et leurs tailles possibles.
 *
 * La liste s'édite entière puis s'enregistre d'un bloc, comme la grille des
 * droits : elle est courte, et un envoi complet évite les demi-listes quand
 * deux administrateurs la touchent en même temps. Les codes des vêtements
 * existants ne bougent pas — les tailles déjà déclarées y restent accrochées.
 */
export default function TaillesVetements() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['referentiels'], queryFn: fetchReferentiels });

  const [liste, setListe] = useState<Vetement[] | null>(null);
  const [nouveauLabel, setNouveauLabel] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // La liste locale part de celle du serveur, puis vit sa vie jusqu'à
  // l'enregistrement.
  useEffect(() => {
    if (liste === null && query.data?.vetements) {
      setListe(query.data.vetements.map((v: Vetement) => ({ ...v, tailles: [...v.tailles] })));
    }
  }, [query.data, liste]);

  const enregistrer = useMutation({
    mutationFn: (vetements: Vetement[]) => updateVetementsAdmin(vetements),
    onSuccess: (d: any) => {
      setNotice({ type: 'success', message: d.message });
      setListe(d.vetements.map((v: Vetement) => ({ ...v, tailles: [...v.tailles] })));
      queryClient.invalidateQueries({ queryKey: ['referentiels'] });
      queryClient.invalidateQueries({ queryKey: ['benevole-referentiel'] });
      setTimeout(() => setNotice(null), 2500);
    },
    onError: (e: Error) => setNotice({ type: 'error', message: e.message }),
  });

  if (query.isPending || liste === null) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const modifier = (index: number, patch: Partial<Vetement>) =>
    setListe((prev) => prev!.map((v, i) => (i === index ? { ...v, ...patch } : v)));

  const ajouterVetement = () => {
    const label = nouveauLabel.trim();
    if (!label) return;
    // Les tailles standard en point de départ : plus vite retirées
    // qu'entièrement retapées.
    setListe((prev) => [...prev!, { label, tailles: ['S', 'M', 'L', 'XL', 'XXL'] }]);
    setNouveauLabel('');
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Les vêtements que chaque bénévole peut déclarer sur sa fiche, avec leurs tailles
        possibles. L'onglet Tailles de la page Membres compte les commandes d'après cette liste.
      </p>

      <div className="space-y-3">
        {liste.map((vetement, index) => (
          <div
            key={vetement.code ?? `nouveau-${index}`}
            className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-2"
          >
            <div className="flex items-center gap-2">
              <input
                value={vetement.label}
                onChange={(e) => modifier(index, { label: e.target.value })}
                className="input-field flex-1 font-medium"
                placeholder="Nom du vêtement"
              />
              <button
                type="button"
                onClick={() => setListe((prev) => prev!.filter((_, i) => i !== index))}
                className="p-2 text-gray-400 hover:text-red-600"
                title="Retirer ce vêtement"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <SaisieTailles
              tailles={vetement.tailles}
              onChange={(tailles) => modifier(index, { tailles })}
            />
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input
          value={nouveauLabel}
          onChange={(e) => setNouveauLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              ajouterVetement();
            }
          }}
          placeholder="Nouveau vêtement (bonnet, gilet, bermuda…)"
          className="input-field flex-1"
        />
        <button type="button" onClick={ajouterVetement} className="btn-outline inline-flex items-center gap-2 whitespace-nowrap">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {notice && (
        <p className={`text-sm ${notice.type === 'success' ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {notice.message}
        </p>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => enregistrer.mutate(liste)}
          disabled={enregistrer.isPending}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {enregistrer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Enregistrer la liste
        </button>
      </div>
    </div>
  );
}
